import React, { useState, useEffect } from 'react';
import { API_CONFIG, buildApiUrl } from '../config/api';

interface Station {
  id: number;
  name: string;
  code: string;
  line_id: number;
  line_name?: string;
  order_in_line?: number;
}

interface Company {
  id: number;
  name: string;
  code: string;
  station_count: number;
}

interface StationPricing {
  id: number;
  company_id: number;
  from_station_id: number;
  to_station_id: number;
  price: number;
  currency: string;
  station_count: number | null;
  distance_km: number | null;
  is_active: boolean;
  valid_from: string | null;
  valid_to: string | null;
  created_at: string;
  updated_at: string;
  from_station_name?: string;
  from_station_code?: string;
  to_station_name?: string;
  to_station_code?: string;
  company_name?: string;
}

interface PricingFormData {
  company_id: number;
  from_station_id: number;
  to_station_id: number;
  price: number;
  currency: string;
  station_count: number | null;
  distance_km: number | null;
  is_active: boolean;
  valid_from: string;
  valid_to: string;
}

const StationPricingManager: React.FC = () => {
  const [pricingList, setPricingList] = useState<StationPricing[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [stations, setStations] = useState<Station[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<PricingFormData>({
    company_id: 0,
    from_station_id: 0,
    to_station_id: 0,
    price: 0,
    currency: 'THB',
    station_count: null,
    distance_km: null,
    is_active: true,
    valid_from: '',
    valid_to: ''
  });

  // Filters
  const [filterCompany, setFilterCompany] = useState<number | null>(null);
  const [filterFromStation, setFilterFromStation] = useState<number | null>(null);
  const [filterToStation, setFilterToStation] = useState<number | null>(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    fetchPricingList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, filterCompany, filterFromStation, filterToStation]);

  const fetchInitialData = async () => {
    try {
      const [companiesRes, stationsRes] = await Promise.all([
        fetch(buildApiUrl(API_CONFIG.ENDPOINTS.PRICING_COMPANIES)),
        fetch(buildApiUrl(API_CONFIG.ENDPOINTS.STATIONS))
      ]);

      const companiesData = await companiesRes.json();
      const stationsData = await stationsRes.json();

      setCompanies(companiesData.companies);

      // Sort stations by line_name first, then by order_in_line within each line, then by name
      const sortedStations = stationsData.stations.sort((a: Station, b: Station) => {
        // First sort by line name
        const lineCompare = (a.line_name || '').localeCompare(b.line_name || '');
        if (lineCompare !== 0) return lineCompare;

        // Then by order_in_line (null values go to end)
        if (a.order_in_line == null && b.order_in_line == null) {
          return a.name.localeCompare(b.name); // fallback to name
        }
        if (a.order_in_line == null) return 1;
        if (b.order_in_line == null) return -1;

        const orderCompare = a.order_in_line - b.order_in_line;
        if (orderCompare !== 0) return orderCompare;

        // Finally by name as fallback
        return a.name.localeCompare(b.name);
      });

      setStations(sortedStations);
    } catch (err) {
      setError('Failed to fetch initial data');
      console.error('Error fetching initial data:', err);
    }
  };

  const fetchPricingList = async () => {
    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams({
        skip: ((currentPage - 1) * itemsPerPage).toString(),
        limit: itemsPerPage.toString()
      });

      if (filterCompany) params.append('company_id', filterCompany.toString());
      if (filterFromStation) params.append('from_station_id', filterFromStation.toString());
      if (filterToStation) params.append('to_station_id', filterToStation.toString());

      const response = await fetch(buildApiUrl(`${API_CONFIG.ENDPOINTS.PRICING}/?${params}`));
      const data = await response.json();
      setPricingList(data.pricing);
      setTotalCount(data.total_count);
    } catch (err) {
      setError('Failed to fetch pricing list');
      console.error('Error fetching pricing list:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (formData.from_station_id === formData.to_station_id) {
      setError('From and To stations cannot be the same');
      return;
    }

    try {
      if (editingId) {
        await fetch(buildApiUrl(`${API_CONFIG.ENDPOINTS.PRICING}/${editingId}`), {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        });
        setSuccess('Pricing updated successfully');
      } else {
        await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.PRICING), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        });
        setSuccess('Pricing created successfully');
      }

      resetForm();
      fetchPricingList();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save pricing');
    }
  };

  const handleEdit = (pricing: StationPricing) => {
    setFormData({
      company_id: pricing.company_id,
      from_station_id: pricing.from_station_id,
      to_station_id: pricing.to_station_id,
      price: pricing.price,
      currency: pricing.currency,
      station_count: pricing.station_count,
      distance_km: pricing.distance_km,
      is_active: pricing.is_active,
      valid_from: pricing.valid_from || '',
      valid_to: pricing.valid_to || ''
    });
    setEditingId(pricing.id);
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this pricing?')) return;

    try {
      await fetch(buildApiUrl(`${API_CONFIG.ENDPOINTS.PRICING}/${id}`), {
        method: 'DELETE'
      });
      setSuccess('Pricing deleted successfully');
      fetchPricingList();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete pricing');
    }
  };

  const resetForm = () => {
    setFormData({
      company_id: 0,
      from_station_id: 0,
      to_station_id: 0,
      price: 0,
      currency: 'THB',
      station_count: null,
      distance_km: null,
      is_active: true,
      valid_from: '',
      valid_to: ''
    });
    setEditingId(null);
    setShowForm(false);
  };

  const getStationName = (stationId: number) => {
    const station = stations.find(s => s.id === stationId);
    return station ? `${station.code} - ${station.name}` : 'Unknown Station';
  };

  const getCompanyName = (companyId: number) => {
    const company = companies.find(c => c.id === companyId);
    return company ? company.name : 'Unknown Company';
  };

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  return (
    <div className="container mx-auto p-6">
      <div className="bg-white shadow-lg rounded-lg p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Station-to-Station Pricing Management</h2>
          <button
            onClick={() => setShowForm(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Add New Pricing
          </button>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6">
            {success}
          </div>
        )}

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Company</label>
            <select
              value={filterCompany || ''}
              onChange={(e) => setFilterCompany(e.target.value ? Number(e.target.value) : null)}
              className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Companies</option>
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Filter by From Station</label>
            <select
              value={filterFromStation || ''}
              onChange={(e) => setFilterFromStation(e.target.value ? Number(e.target.value) : null)}
              className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Stations</option>
              {stations.map((station) => (
                <option key={station.id} value={station.id}>
                  {station.line_name ? `[${station.line_name}] ` : ''}{station.code} - {station.name}{station.order_in_line ? ` (#${station.order_in_line})` : ''}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Filter by To Station</label>
            <select
              value={filterToStation || ''}
              onChange={(e) => setFilterToStation(e.target.value ? Number(e.target.value) : null)}
              className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Stations</option>
              {stations.map((station) => (
                <option key={station.id} value={station.id}>
                  {station.line_name ? `[${station.line_name}] ` : ''}{station.code} - {station.name}{station.order_in_line ? ` (#${station.order_in_line})` : ''}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Pricing Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <h3 className="text-lg font-bold text-gray-900 mb-4">
                {editingId ? 'Edit Pricing' : 'Add New Pricing'}
              </h3>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                  <select
                    value={formData.company_id}
                    onChange={(e) => setFormData({...formData, company_id: Number(e.target.value)})}
                    required
                    className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  >
                    <option value={0}>Select Company</option>
                    {companies.map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">From Station</label>
                  <select
                    value={formData.from_station_id}
                    onChange={(e) => setFormData({...formData, from_station_id: Number(e.target.value)})}
                    required
                    className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  >
                    <option value={0}>Select From Station</option>
                    {stations.map((station) => (
                      <option key={station.id} value={station.id}>
                        {station.code} - {station.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">To Station</label>
                  <select
                    value={formData.to_station_id}
                    onChange={(e) => setFormData({...formData, to_station_id: Number(e.target.value)})}
                    required
                    className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  >
                    <option value={0}>Select To Station</option>
                    {stations.map((station) => (
                      <option key={station.id} value={station.id}>
                        {station.code} - {station.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Price</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.price}
                      onChange={(e) => setFormData({...formData, price: Number(e.target.value)})}
                      required
                      className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                    <select
                      value={formData.currency}
                      onChange={(e) => setFormData({...formData, currency: e.target.value})}
                      className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="THB">THB</option>
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Station Count</label>
                    <input
                      type="number"
                      value={formData.station_count || ''}
                      onChange={(e) => setFormData({...formData, station_count: e.target.value ? Number(e.target.value) : null})}
                      className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Distance (km)</label>
                    <input
                      type="number"
                      step="0.1"
                      value={formData.distance_km || ''}
                      onChange={(e) => setFormData({...formData, distance_km: e.target.value ? Number(e.target.value) : null})}
                      className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                      className="rounded"
                    />
                    <span className="text-sm font-medium text-gray-700">Active</span>
                  </label>
                </div>

                <div className="flex justify-end space-x-2 pt-4">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    {editingId ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Pricing List */}
        <div className="overflow-x-auto">
          <table className="min-w-full table-auto border-collapse border border-gray-300">
            <thead className="bg-gray-100">
              <tr>
                <th className="border border-gray-300 px-4 py-2 text-left">Company</th>
                <th className="border border-gray-300 px-4 py-2 text-left">From Station</th>
                <th className="border border-gray-300 px-4 py-2 text-left">To Station</th>
                <th className="border border-gray-300 px-4 py-2 text-left">Price</th>
                <th className="border border-gray-300 px-4 py-2 text-left">Station Count</th>
                <th className="border border-gray-300 px-4 py-2 text-left">Distance</th>
                <th className="border border-gray-300 px-4 py-2 text-left">Status</th>
                <th className="border border-gray-300 px-4 py-2 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    <p className="mt-2 text-gray-600">Loading...</p>
                  </td>
                </tr>
              ) : pricingList.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-8 text-gray-500">
                    No pricing data found
                  </td>
                </tr>
              ) : (
                pricingList.map((pricing) => (
                  <tr key={pricing.id} className="hover:bg-gray-50">
                    <td className="border border-gray-300 px-4 py-2">
                      {pricing.company_name || getCompanyName(pricing.company_id)}
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      {pricing.from_station_name ?
                        `${pricing.from_station_code} - ${pricing.from_station_name}` :
                        getStationName(pricing.from_station_id)
                      }
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      {pricing.to_station_name ?
                        `${pricing.to_station_code} - ${pricing.to_station_name}` :
                        getStationName(pricing.to_station_id)
                      }
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      {pricing.price} {pricing.currency}
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      {pricing.station_count || '—'}
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      {pricing.distance_km ? `${pricing.distance_km} km` : '—'}
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      <span className={`px-2 py-1 rounded text-xs ${
                        pricing.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {pricing.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(pricing)}
                          className="text-blue-600 hover:text-blue-800 text-sm"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(pricing.id)}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row justify-between items-center mt-6 gap-4">
            <div className="text-sm text-gray-700">
              Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount} results
            </div>
            <div className="flex flex-wrap items-center justify-center gap-1">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Previous
              </button>

              {/* First page */}
              {currentPage > 3 && (
                <>
                  <button
                    onClick={() => setCurrentPage(1)}
                    className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50"
                  >
                    1
                  </button>
                  {currentPage > 4 && (
                    <span className="px-2 py-1 text-sm text-gray-500">...</span>
                  )}
                </>
              )}

              {/* Page numbers around current page */}
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(page => {
                  // Show pages within 2 of current page
                  return page >= currentPage - 2 && page <= currentPage + 2;
                })
                .map(page => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-1 text-sm border border-gray-300 rounded ${
                      currentPage === page ? 'bg-blue-600 text-white' : 'hover:bg-gray-50'
                    }`}
                  >
                    {page}
                  </button>
                ))}

              {/* Last page */}
              {currentPage < totalPages - 2 && (
                <>
                  {currentPage < totalPages - 3 && (
                    <span className="px-2 py-1 text-sm text-gray-500">...</span>
                  )}
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50"
                  >
                    {totalPages}
                  </button>
                </>
              )}

              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StationPricingManager;